import React, { useState } from "react";
import Pagination from "../generic/Pagination";

interface GenericTableProps {
    columns: string[];
    data: Record<string, any>[];
    tableTitle?: string;
    currentPage?: number;
    onPageChange: (page: number) => void;
}

const GenericTable: React.FC<GenericTableProps> = ({
                                                       columns,
                                                       data,
                                                       tableTitle,
                                                       currentPage = 1,
                                                       onPageChange,
                                                   }) => {
    const [ sortConfig, setSortConfig ] = useState<{ column: string; direction: "asc" | "desc" } | null>(null);
    const [ pageSize, setPageSize ] = useState(10);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
        }).format(date);
    };

    const handleSort = (column: string) => {
        setSortConfig((prevConfig) => {
            if (prevConfig && prevConfig.column === column) {
                return { column, direction: prevConfig.direction === "asc" ? "desc" : "asc" };
            }
            return { column, direction: "asc" };
        });
    };

    const sortedData = [ ...data ].sort((a, b) => {
        if (!sortConfig) return 0;

        const { column, direction } = sortConfig;
        const aValue = a[column] || "";
        const bValue = b[column] || "";

        if (typeof aValue === "number" && typeof bValue === "number") {
            return direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
            return direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }

        return 0;
    });

    const startIndex = (currentPage - 1) * pageSize;
    const paginatedData = sortedData.slice(startIndex, startIndex + pageSize);

    const renderSortIcon = (column: string) => {
        if (!sortConfig || sortConfig.column !== column) {
            return <span>  ⇅</span>;
        }

        return (
            <span>
                {sortConfig.direction === "asc" ? "▲" : "▼"}
            </span>
        );
    };

    return (
        <div>
            <div>
                {tableTitle && <h3 className={"font-semibold mb-4 text-lg"}>{tableTitle}</h3>}
                <div className="overflow-x-auto">

                    <table>
                        <thead>
                        <tr>
                            {columns.map((column, index) => (
                                <th
                                    key={index}
                                    onClick={() => handleSort(column)}
                                >
                                    {column}
                                    {renderSortIcon(column)}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((column, colIndex) => (
                                        <td
                                            key={colIndex}
                                        >
                                            {(column === "Created At" || column === "Updated At") && row[column]
                                                ? formatDate(row[column])
                                                : row[column] || "-"}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                >
                                    No data available
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    total={sortedData.length}
                    currentPage={currentPage}
                    onPageChange={onPageChange}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                />
            </div>
        </div>
    );
};

export default GenericTable;